import dayjs from 'dayjs';
import { locationBaseSchema, passengerCountWithMaxSchemaObject, zo } from 'remote/global';
import { DaysOfWeek } from 'utils/data/data-types';

export const MINIMUM_STOPOVERS = 4;
const emptyDeparture = {
  clientDepartureTime: '',
  licensePlate: '',
  schedule: {
    daysOfWeek: [],
    startsOn: '',
    endsOn: '',
  },
};

const departureSchema = zo
  .object({
    clientDepartureTime: zo.string().min(1),
    clientReturnTime: zo.string().or(zo.null()).optional(),
    returnTripVisible: zo.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    // If returnTripVisible is true, clientReturnTime should not be null
    if (data.returnTripVisible && !data.clientReturnTime) {
      ctx.addIssue({
        code: zo.ZodIssueCode.custom,
        path: ['clientReturnTime'],
      });
    }
  });

const corporateCharterItinerarySchema = passengerCountWithMaxSchemaObject.and(
  zo.object({
    stops: zo.array(locationBaseSchema),
    departures: zo.array(departureSchema),
  })
);
const corporateCharterScheduleStopsSchema = zo.object({
  distanceKmFromPrevious: zo.number(),
  minutesFromPrevious: zo.number(),
  location: locationBaseSchema,
  time: zo.string().min(1),
  name: zo.string().min(1),
});

const corporateCharterScheduleSchema = zo.object({
  encodedPolylines: zo.array(zo.string()),
  stops: zo.array(corporateCharterScheduleStopsSchema),
  departures: zo.array(departureSchema),
});

const lineDetailsDeparturesSchema = departureSchema.and(
  zo.object({
    licensePlate: zo.string().min(1),
    schedule: zo
      .object({
        daysOfWeek: zo.array(zo.nativeEnum(DaysOfWeek)).min(1),
        startsOn: zo.string().date(),
        endsOn: zo.string().date(),
      })
      .superRefine((data, ctx) => {
        if (new Date(data.startsOn) <= new Date()) {
          ctx.addIssue({
            code: zo.ZodIssueCode.custom,
            params: { i18n: { key: 'dateInFuture' } },
            path: [`startsOn`],
          });
        }
        const { startsOn, endsOn } = data;
        const isToBeforeFrom = dayjs(endsOn) < dayjs(startsOn);
        if (isToBeforeFrom) {
          ['startsOn', 'endsOn'].map((path, idx) =>
            ctx.addIssue({
              code: zo.ZodIssueCode.custom,
              params: {
                i18n: {
                  key: idx === 0 ? 'dateAfterAnother_with_value' : 'dateBeforeAnother_with_value',
                  values: {
                    other:
                      idx === 0
                        ? 'paths.departures.schedule.endsOn'
                        : 'paths.departures.schedule.startsOn',
                  },
                },
              },
              path: [path],
            })
          );
        }
      }),
  })
);

const lineDetailsSchema = zo.object({
  departures: zo.array(lineDetailsDeparturesSchema),
  lineName: zo.string().min(1),
  ridePriceExclVat: zo
    .number()
    .refine((value) => value > 0, { params: { i18n: { key: 'required' } } }),
  ridePricePerKmEur: zo
    .number()
    .refine((value) => value > 0, { params: { i18n: { key: 'required' } } }),
});

const corporateCharterClientDetailsSchema = zo.object({
  clientId: zo.coerce.string().min(1).pipe(zo.coerce.number()),
});

type CorporateCharterItinerarySchemaType = zo.infer<typeof corporateCharterItinerarySchema>;
type CorporateCharterScheduleSchemaType = zo.infer<typeof corporateCharterScheduleSchema>;
type CorporateCharterScheduleStopsSchemaType = zo.infer<typeof corporateCharterScheduleStopsSchema>;
type LineDetailsSchemaType = zo.infer<typeof lineDetailsSchema>;
type LineDetailsDeparturesSchemaType = zo.infer<typeof lineDetailsDeparturesSchema>;
type CorporateCharterClientDetailsSchemaType = zo.infer<typeof corporateCharterClientDetailsSchema>;
type DeparturesSchemaType = zo.infer<typeof departureSchema>;

export {
  corporateCharterClientDetailsSchema,
  corporateCharterItinerarySchema,
  corporateCharterScheduleSchema,
  emptyDeparture,
  lineDetailsSchema,
};
export type {
  CorporateCharterClientDetailsSchemaType,
  CorporateCharterItinerarySchemaType,
  CorporateCharterScheduleSchemaType,
  CorporateCharterScheduleStopsSchemaType,
  DeparturesSchemaType,
  LineDetailsDeparturesSchemaType,
  LineDetailsSchemaType,
};
